import PropTypes from 'prop-types';

import React, { Component } from 'react';
import _ from 'lodash';
import classNames from 'classnames';

import appConfig from '@nerdwallet/app-config';
import classes from '@nerdwallet/base-styles/classes';
import Pagination from '@nerdwallet/react-pagination';
import {
  LoadingSpinner,
  AdvertiserDisclosure,
  Type,
} from '@nerdwallet/currency';
import SearchInput from '@nerdwallet/nw-search-input';

import {
  elementInteraction,
  formInputChanged,
} from '@nerdwallet/nw-client-lib/tracking';
import Carousel from '@nerdwallet/react-carousel';

import SearchResults from './search-results';
import SearchFacetFilter from './search-facet-filter';
import NoResults from './no-results';
import nwApiSdk from '~/nw-api-sdk';
import NavigateRight from '~/client/components/svg/navigate-right';
import NavigateLeft from '~/client/components/svg/navigate-left';
import ProductCard from '~/client/components/product-card/product-card';

const DIRECTION = {
  UP: 'UP',
  DOWN: 'DOWN',
};

class SearchPage extends Component {
  static renderLoading() {
    return (
      <div
        className={classes([
          'text-align-center',
          'margin-horizontal-auto',
          'margin-vertical-5',
        ])}
        style={{
          width: 125,
          height: 125,
        }}
      >
        <LoadingSpinner type="n" size={125} />
      </div>
    );
  }

  static trackPageNavigation(page, element) {
    elementInteraction({
      elementName: 'nw-search::pagination',
      elementContent: element,
      pageNumber: page,
    });
  }

  static trackSearchCount(term, count) {
    formInputChanged({
      formName: 'nw-search',
      fieldName: 'result-count',
      fieldValue: count,
      additionalData: JSON.stringify({ 'search-term': term }),
    });
  }

  constructor(props) {
    super(props);

    this.searchServicesBaseUri = appConfig.NW_SEARCH_SERVICE_BASE_URI;

    this.handleFacetChange = this.handleFacetChange.bind(this);
    this.handleNavigationPreviousClick =
      this.handleNavigationPreviousClick.bind(this);
    this.handleNavigationNextClick = this.handleNavigationNextClick.bind(this);
    this.handlePageClick = this.handlePageClick.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.onOverviewRef = this.onOverviewRef.bind(this);
    this.onHeaderRef = this.onHeaderRef.bind(this);
    this.onSearchPageRef = this.onSearchPageRef.bind(this);
    this.onAdvertiserDisclosureRef = this.onAdvertiserDisclosureRef.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);
  }

  componentDidMount() {
    const count = _.get(this.props.search, 'results.count', 0);
    const searchTerm = _.get(this.props.search, 'query.q', '');
    SearchPage.trackSearchCount(searchTerm, count);
    window.addEventListener('scroll', this.onWindowScroll);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      _.get(this.props, 'search.results.count') !==
        _.get(nextProps, 'search.results.count') ||
      _.get(this.props, 'search.query.q') !== _.get(nextProps, 'search.query.q')
    ) {
      const count = _.get(nextProps.search, 'results.count', 0);
      const searchTerm = _.get(nextProps.search, 'query.q', '');
      SearchPage.trackSearchCount(searchTerm, count);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onWindowScroll);
  }

  onWindowScroll() {
    const { scrollY } = window;
    if (scrollY < this.currentScrollY) {
      // scrolling up
      if (this.currentScrollDirection !== DIRECTION.UP) {
        // start scrolling up
        this.currentScrollDirection = DIRECTION.UP;
        this.onScrollUpStart();
      } else if (scrollY < this.scrollUpBreakpointStuck) {
        // scroll up past the breakpoint
        this.onScrollUpStuck();
      } else if (scrollY < this.scrollUpBreakpointRelative) {
        // set relative, same as scrolling down
        this.scrollUpBreakpointRelative = 0;
        this.onScrollDownStart();
      }
    } else if (
      scrollY > this.currentScrollY &&
      this.currentScrollDirection !== DIRECTION.DOWN
    ) {
      // start scrolling down
      this.currentScrollDirection = DIRECTION.DOWN;
      this.onScrollDownStart();
    }
    this.currentScrollY = scrollY;
  }

  onScrollUpStart() {
    // Place header right above viewport to scroll in gracefully from the top
    const header = this.headerRef;
    const searchPage = this.searchPageRef;
    const headerStyle = window.getComputedStyle(header);

    // get height what we want to stick (full height - overview height - advertiser disclosure height)
    const headerHeight =
      header.offsetHeight -
      this.getOverviewHeight() -
      this.advertiserDisclosureRef.offsetHeight;

    // get current position offset of the header.
    // If the user scrolls back up before fully scrolling down and the header is still in view, we don't want it to jump
    const curTopOffset =
      headerStyle.position === 'relative'
        ? parseFloat(headerStyle.top) || 0
        : 0;

    // get the position of where the header should be, just above the viewport
    const newTopOffset =
      window.scrollY - searchPage.offsetTop - header.offsetHeight;

    const finalTopOffset = Math.max(newTopOffset, curTopOffset);

    // set scrollY position of when we want to change from relative to sticky
    this.scrollUpBreakpointStuck =
      window.scrollY - (headerHeight - (finalTopOffset - newTopOffset));

    if (window.scrollY < this.scrollUpBreakpointStuck) {
      // This means the scroll position jumped up higher than the height of the header
      this.onScrollUpStuck();
      return;
    }

    header.classList.add(classes(['position-relative']));
    header.classList.remove(classes(['position-sticky']));
    Object.assign(header.style, {
      top: `${finalTopOffset}px`,
    });
  }

  onScrollUpStuck() {
    // Set header sticky
    this.scrollUpBreakpointStuck = 0;

    // set breakpoint for when scroll reaches top
    // change to position relative at this breakpoint to fix bug in safari and ios webkit
    this.scrollUpBreakpointRelative =
      this.searchPageRef.offsetTop +
      this.getOverviewHeight() +
      this.advertiserDisclosureRef.offsetHeight;

    this.headerRef.classList.add(classes(['position-sticky']));
    this.headerRef.classList.remove(classes(['position-relative']));

    Object.assign(this.headerRef.style, {
      top: `${
        Math.ceil(
          this.getOverviewHeight() + this.advertiserDisclosureRef.offsetHeight
        ) * -1
      }px`,
    });
  }

  onScrollDownStart() {
    const header = this.headerRef;
    // is the header already in the transitional position?
    if (window.getComputedStyle(header).position === 'relative') return;
    header.classList.add(classes(['position-relative']));
    header.classList.remove(classes(['position-sticky']));
    Object.assign(header.style, {
      top: `${Math.max(
        window.scrollY -
          this.searchPageRef.offsetTop -
          this.getOverviewHeight() -
          this.advertiserDisclosureRef.offsetHeight,
        0
      )}px`,
    });
  }

  onSearchChanged({
    query = this.props.query,
    page = this.props.page,
    facets = this.props.facets,
    suggestion = this.props.suggestion,
  }) {
    this.props.history.push(
      `${appConfig.BASE_URI}/results?q=${encodeURIComponent(
        query
      )}&p=${page}&f=${encodeURIComponent(JSON.stringify(facets))}${
        suggestion ? '&s=1' : ''
      }`
    );
  }

  onOverviewRef(ref) {
    this.overviewRef = ref;
  }

  onSearchPageRef(ref) {
    this.searchPageRef = ref;
  }

  onAdvertiserDisclosureRef(ref) {
    this.advertiserDisclosureRef = ref;
  }

  onHeaderRef(ref) {
    this.headerRef = ref;
  }

  getOverviewHeight() {
    return (
      this.overviewRef.offsetHeight +
      parseFloat(window.getComputedStyle(this.overviewRef).marginTop)
    );
  }

  // eslint-disable-next-line react/sort-comp
  handleFacetChange({ key, value }) {
    const newFacets = _.cloneDeep(this.props.facets);

    newFacets[key] = value;

    if (!newFacets[key].length) {
      delete newFacets[key];
    }

    this.onSearchChanged({
      facets: newFacets,
      page: 0,
    });
  }

  handleNavigationNextClick() {
    const { page } = this.props;
    const { count } = _.get(this.props.search, 'results', 0);
    if ((page + 1) * 10 < count) {
      this.handlePageClick(page + 2);
    }
  }

  handleNavigationPreviousClick() {
    const { page } = this.props;
    const animatedScrollTo = require('animated-scrollto'); //eslint-disable-line
    if (page > 0) {
      this.handlePageClick(page);
    }
  }

  handlePageClick(pagPage, action = 'pageNumber') {
    let paginationPage = pagPage;
    paginationPage -= 1;
    const animatedScrollTo = require('animated-scrollto'); //eslint-disable-line

    animatedScrollTo(document.documentElement, 0, 0);
    this.onSearchChanged({
      page: paginationPage,
    });
    SearchPage.trackPageNavigation(paginationPage, action);
  }

  handleSearch(val, { suggestion = false } = {}) {
    this.onSearchChanged({ query: val, page: 0, suggestion, facets: {} });
  }

  renderNavigation() {
    const { count } = _.get(this.props.search, 'results', 0);
    const { page, resultsPerPage } = this.props;
    const totalPages = Math.ceil(count / resultsPerPage);

    if (totalPages < 2) return null;

    const renderPrevious = () => {
      if (page > 0) {
        return (
          <div className="search-navigation-links-previous">
            <button
              type="button"
              onClick={this.handleNavigationPreviousClick}
              onKeyPress={this.handleNavigationPreviousClick}
              aria-label="Previous"
              tabIndex={0}
            >
              <NavigateLeft className="search-navigation-previous-arrow" />
            </button>
          </div>
        );
      }

      return null;
    };
    const renderNext = () => {
      if ((page + 1) * resultsPerPage < count) {
        return (
          <div className="search-navigation-links-next">
            <button
              type="button"
              onClick={this.handleNavigationNextClick}
              onKeyPress={this.handleNavigationNextClick}
              aria-label="Next"
              tabIndex={0}
            >
              <NavigateRight className="search-navigation-next-arrow" />
            </button>
          </div>
        );
      }

      return null;
    };

    return (
      <nav className="search-navigation">
        <div className="search-navigation-links" role="navigation">
          {renderPrevious()}
          <div className="currentPage">Page {page + 1}</div>
          {renderNext()}
          <Pagination
            min={1}
            max={totalPages}
            current={page + 1}
            onClick={this.handlePageClick}
          />
        </div>
      </nav>
    );
  }

  renderPageCount() {
    const { page, resultsPerPage } = this.props;
    const { count, hits, pending } = _.get(this.props.search, 'results', {});
    const firstResult = Math.round(page) * resultsPerPage + 1;
    const endResult = firstResult + hits.length - 1;

    return (
      <div className="search-page-results-summary">
        {(count > 0 || pending) && (
          <div>
            <Type size={1} bold uppercase light>
              You searched for
            </Type>
            <Type size={2} bold>
              {_.get(this.props, 'search.query.q')}
            </Type>
          </div>
        )}
        <Type size={2} className="search-page-count">
          Showing results{' '}
          <Type inline bold>
            {endResult > 0 ? `${firstResult} to ` : ''}
            {endResult}
          </Type>{' '}
          of{' '}
          <Type inline bold>
            {count}
          </Type>
        </Type>
      </div>
    );
  }

  renderResults() {
    const {
      search: {
        results: { hits },
      },
      query,
      page,
    } = this.props;

    return <SearchResults {...{ hits, query, page }} />;
  }

  renderProducts() {
    const {
      productSearch: {
        productResults: { offers },
      },
      query,
    } = this.props;

    const settings = {
      slidesToShow: 3,
      focusOnSelect: false,
      variableWidth: true,
      swipeToSlide: true,
      afterChange: () => {
        window.dispatchEvent(new Event('scroll'));
      },
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
          },
        },
        {
          breakpoint: 720,
          settings: {
            slidesToShow: 1,
            arrows: false,
          },
        },
      ],
    };

    return (
      <div className="search-carousel">
        <Type size={1} uppercase color="neutral-light">
          Credit Cards
        </Type>
        <Carousel {...settings} id="search-products-carousel">
          {offers.map((offer, index) => (
            <ProductCard data={offer} position={index + 1} query={query} />
          ))}
        </Carousel>
      </div>
    );
  }

  renderErrorMessage(useQuery, error) {
    return (
      <NoResults query={this.props.query} useQuery={useQuery} error={error} />
    );
  }

  renderOverview() {
    const facets = _.get(this.props, 'search.results.facets');
    const selectedFacets = _.get(this.props, 'facets');

    return (
      <div className="search-page-overview" ref={this.onOverviewRef}>
        {this.renderPageCount()}
        <SearchFacetFilter
          // This is all facets for the current search query, returned with the search results
          facets={facets}
          // These are the currently selected facets, as represented by the URL query param 'f'
          // The URL is used as the source of truth so it doesn't wait for the server response before updating the checkbox state
          selectedFacets={selectedFacets}
          onFacetChange={this.handleFacetChange}
        />
      </div>
    );
  }

  renderSearchInput = ({ id, placeholder, className }) => {
    return (
      <SearchInput
        id={id}
        className={classNames('search-input', className)}
        value={this.props.query}
        onSearch={this.handleSearch}
        nwApiSdk={nwApiSdk}
        searchServicesBaseUri={this.searchServicesBaseUri}
        showClearInput
        inputClearColor="neutral-dark"
        autoFocus={false}
        placeholder={placeholder}
        inputProps={{ 'aria-label': 'Search the NerdWallet site' }}
      />
    );
  };

  render() {
    const {
      search: {
        query: { query },
        results: { pending, count: resultCount } = {},
      } = {},
      productSearch: {
        productResults: { pending: productPending, offers: products },
      },
    } = this.props;

    const hasProducts = !!products.length;
    const hasResults = !!resultCount;
    const noResults = !pending && query && !hasResults;
    const hasError = !pending && !hasResults && this.props.search.error;

    return (
      <div
        ref={this.onSearchPageRef}
        className={classNames('search-page', {
          'no-results': !hasProducts && (noResults || hasError),
        })}
      >
        <div
          ref={this.onHeaderRef}
          className={classNames(
            'search-page-header',
            classes(['position-relative'])
          )}
        >
          <div
            className="advertiser-disclosure"
            ref={this.onAdvertiserDisclosureRef}
          >
            <AdvertiserDisclosure />
          </div>
          <div className="search-input-sticky-container">
            {this.renderSearchInput({
              className: 'search-input-wide',
              id: 'searchInputWide',
              placeholder: 'Try something like "Best saving account?"',
            })}
            {this.renderSearchInput({
              className: 'search-input-not-wide',
              id: 'searchInputNotWide',
              placeholder: 'Try "Best saving account?"',
            })}
          </div>
          {query && this.renderOverview()}
        </div>
        <div className="search-page-results-panel">
          {/* Show loading screen until products and articles have ben returned */}
          {pending && productPending && SearchPage.renderLoading()}
          {/* If products and articles have returned render them */}
          {!pending && !productPending && hasProducts && this.renderProducts()}
          {!pending && !productPending && hasResults && this.renderResults()}
          {/* END render check */}
          {/* Render no result page with error message when an error happened */}
          {hasError && this.renderErrorMessage(false, true)}
          {/* Render no result page if search did not produce any results */}
          {noResults && !hasProducts && this.renderErrorMessage(true, false)}
          {/* render pagination if there are article results and no pending fetches */}
          {!pending && !productPending && hasResults && this.renderNavigation()}
        </div>
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
SearchPage.displayName = 'SearchPage';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
SearchPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  search: PropTypes.object.isRequired,
  productSearch: PropTypes.object.isRequired,
  resultsPerPage: PropTypes.number.isRequired,
  query: PropTypes.string,
  page: PropTypes.number,
  facets: PropTypes.object,
  suggestion: PropTypes.bool,
  history: PropTypes.object,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
SearchPage.defaultProps = {
  facets: {},
  page: 0,
};

export default SearchPage;
